import React from 'react';
import _ from 'lodash';
import { createTheme } from '@this/constants/themes';
import { withTheme } from 'styled-components';
import { Badge, makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { Link } from '@this/shared/ui/navigations/link';

import type Trip from '@this/domain/trip/trip2';
import SearchIconTemplate from '../../reserve_trip/select/search_icon/search_icon.template';

interface Props {
  trip: Trip;
  user: any;
  openDraftSearch: (trip: any) => void;
  handleDownloadClick: (id: number, e: React.MouseEvent<HTMLElement>) => void;
  handleCancelClick: (id: number, e: React.MouseEvent<HTMLElement>) => void;
  handleExicClick: (id: number, exicUri: string, e: React.MouseEvent<HTMLElement>) => void;
  handleBusinessEkinetClick: (id: number, businessEkinetUri: string, e: React.MouseEvent<HTMLElement>) => void;
  showConfirmationModal?: (trip: any) => void;
  handleTicketingClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleTripCancelClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleTripRequestApprovalClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleTicketingDisableClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleShowFlightSeatMapModal: () => void;
  theme: { themeClass: string };
  simpleRequestType: boolean;
  isReceipt?: boolean;
}

const TripCardRightTemplate: React.SFC<Props> = ({
  trip,
  user,
  openDraftSearch,
  handleCancelClick,
  showConfirmationModal,
  handleTicketingClick,
  handleDownloadClick,
  handleTripCancelClick,
  handleExicClick,
  handleBusinessEkinetClick,
  handleTripRequestApprovalClick,
  handleTicketingDisableClick,
  handleShowFlightSeatMapModal,
  simpleRequestType,
  theme,
  isReceipt
}) => {
  const classes = useStyles();
  const history = useHistory();
  const styles = getStyles(trip.receiptRevisionStatus);

  const canRequestRevision =
    trip.userId === user.id ||
    (trip.travelerInformations &&
      trip.travelerInformations.length > 0 &&
      trip.travelerInformations[0].userId === user.id);

  return (
    <div style={styles.cardFootIn}>
      {trip.draft ? (
        <div style={styles.rightButtons}>
          {trip.userId === user.id && (
            <Link
              href={`/${user && user.organization.use_non_order_item ? '' : 'draft_'}trips/${trip.id}/edit`}
              style={styles.rightButtonLink}
            >
              編集
            </Link>
          )}
          {user && !user.organization.use_non_order_item && (
            <Link style={styles.rightButtonColored} onClick={() => openDraftSearch(trip)}>
              <div style={styles.searchIcon}>
                <SearchIconTemplate color="#fff" />
              </div>
              <span>予約</span>
            </Link>
          )}
        </div>
      ) : isReceipt ? (
        <>
          {(() => {
            const statusTextMap: Partial<Record<string, string>> = {
              revising: '破棄訂正中',
              revised: '破棄訂正済',
              none: '破棄訂正なし'
            };
            const statusText = trip.receiptRevisionStatus
              ? statusTextMap[trip.receiptRevisionStatus] ?? null
              : null;

            return (
              <div style={styles.statusContainer}>
                <span style={styles.statusLabel}>{statusText || '-'}</span>
              </div>
            );
          })()}
          <div style={styles.rightButtons}>
            <Link
              onClick={e => {
                e.preventDefault();
                if (canRequestRevision) {
                  history.push(`/trip_receipts/${trip.id}/revision_request`);
                }
              }}
              style={{
                ...styles.rightButton,
                cursor: canRequestRevision ? 'pointer' : 'not-allowed',
                opacity: canRequestRevision ? 1 : 0.5
              }}
            >
              領収書訂正依頼
            </Link>
          </div>
        </>
      ) : (
        <div style={styles.rightButtons}>
          {simpleRequestType && (trip.currentApprovalStatus === 1 || _.isEmpty(trip.inAdvanceApprovals)) && (
            <>
              {trip && trip.exicUri && trip.exicUri.length > 0 && (
                <Link style={styles.rightButton} onClick={e => handleExicClick(trip.id, trip.exicUri || '', e)}>
                  JR東海EX予約(予約画面起動)
                </Link>
              )}
              {trip && trip.businessEkinetUri && trip.businessEkinetUri.length > 0 && (
                <Link
                  style={styles.rightButton}
                  onClick={e => handleBusinessEkinetClick(trip.id, trip.businessEkinetUri || '', e)}
                >
                  ビジネスえきねっと予約(予約画面起動)
                </Link>
              )}
            </>
          )}

          {trip.userId === user.id &&
            !simpleRequestType &&
            user.organization.use_non_order_item &&
            (trip.currentApprovalStatus === 1 || _.isEmpty(trip.inAdvanceApprovals) ? (
              <Link href={`/trips/${trip.id}/detail`} style={styles.rightButtonLink}>
                詳細
              </Link>
            ) : trip.status !== 5 && !trip.isApproveCanceled ? (
              <Link href={`/trips/${trip.id}/edit`} style={styles.rightButtonLink}>
                編集
              </Link>
            ) : null)}
          {trip.inAdvanceApprovals && trip.status !== 8 && trip.status !== 9 && trip.status !== 10 && (
            <>
              {showConfirmationModal &&
                trip.currentOrder &&
                trip.currentOrder.travelerConfirmationStatus === 'requested' && (
                  <Link
                    style={styles.rightButton}
                    className="ticket-button"
                    onClick={() => showConfirmationModal(trip)}
                  >
                    見積もり承諾
                  </Link>
                )}
              {handleTicketingClick &&
                trip.ticketingRequestable &&
                (!trip.order?.isTicketingExpired ? (
                  <Link
                    style={styles.rightButton}
                    className="ticket-button"
                    onClick={e => handleTicketingClick(e)}
                  >
                    発券依頼
                  </Link>
                ) : (
                  <Link
                    style={styles.disableRightButton}
                    className="ticket-button"
                    disabled
                    onClick={e => handleTicketingDisableClick(e)}
                  >
                    発券依頼
                  </Link>
                ))}
              {handleShowFlightSeatMapModal && trip.seatReservationEnable && (
                <Link
                  style={styles.rightButton}
                  className="ticket-button"
                  onClick={() => handleShowFlightSeatMapModal()}
                >
                  航空便座席指定
                </Link>
              )}
              {trip.status !== 5 && trip.status !== 6 && trip.currentApprovalStatus === 0 && (
                <Link style={styles.rightButton} onClick={e => handleCancelClick(trip.id, e)}>
                  申請を取消
                </Link>
              )}
              {(trip.currentApprovalStatus === 1 || _.isEmpty(trip.inAdvanceApprovals)) && (
                <>
                  {trip.status === 0 &&
                    trip.currentOrder &&
                    trip.currentOrder.travelerConfirmationStatus === 'confirmed' &&
                    !user.organization.freee_workflow && (
                      <Link style={styles.rightButton} onClick={e => handleTripRequestApprovalClick(e)}>
                        承認申請
                      </Link>
                    )}
                  {!simpleRequestType && trip.isEndTimeInFuture && (
                    <Link style={styles.rightButton} className="cancel" onClick={e => handleTripCancelClick(e)}>
                      一括キャンセル依頼
                    </Link>
                  )}
                  {trip.currentOrder && (
                    <Badge
                      variant="dot"
                      color="error"
                      invisible={!trip.hasUnreadReplyRequiredMessages}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                      classes={{
                        dot: classes.badgeDot
                      }}
                      className={classes.badgeContainer}
                    >
                      <Link
                        href={`/trips/${trip.id}/messages`}
                        style={{ ...styles.rightButtonLink, width: '100%' }}
                      >
                        チャット
                      </Link>
                    </Badge>
                  )}
                  {/* AIトラベルとマイナビのステージ環境のみでの提供 */}
                  {(theme.themeClass === 'aitravel' || theme.themeClass === 'mynavi') && (
                    <Link style={styles.rightButton} onClick={e => handleDownloadClick(trip.id, e)}>
                      CSVダウンロード
                    </Link>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  badgeDot: {
    height: 16,
    minWidth: 16,
    borderRadius: 8
  },
  badgeContainer: {
    width: '100%'
  }
});

function getStyles(receiptRevisionStatus: string | number | undefined) {
  const theme = createTheme(1);

  const rightButtonBase: React.CSSProperties = {
    padding: '8px 14px',
    background: '#ffffff',
    border: `solid 1px ${theme.accentColor}`,
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 0 10px 0',
    lineHeight: '150%',
    width: '100%',
    boxSizing: 'border-box'
  };

  return {
    cardFootIn: {
      display: 'block'
    } as React.CSSProperties,

    rightButtons: {
      marginLeft: 'auto'
    } as React.CSSProperties,

    rightButton: {
      ...rightButtonBase
    } as React.CSSProperties,

    rightButtonLink: {
      ...rightButtonBase,
      textDecoration: 'none'
    } as React.CSSProperties,

    rightButtonColored: {
      ...rightButtonBase,
      display: 'flex',
      background: theme.accentColor,
      color: '#ffffff'
    } as React.CSSProperties,

    disableRightButton: {
      padding: '8px 14px',
      background: theme.grayBgColor,
      color: '#aaa',
      borderRadius: '14px',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 'bold',
      display: 'block',
      marginLeft: '5px',
      cursor: 'default'
    } as React.CSSProperties,

    searchIcon: {
      width: '12px',
      height: '12px',
      margin: '1px 3px 0 0'
    } as React.CSSProperties,

    statusContainer: {
      margin: '0 0 8px 0',
      padding: '8px 14px',
      background: theme.grayBgColor,
      borderRadius: '14px',
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px'
    } as React.CSSProperties,

    statusLabel: {
      margin: 0,
      padding: 0,
      fontWeight: 'bold',
      // 青は旅程statusのArrangingの色に合わせた
      color: receiptRevisionStatus === 'revising' ? theme.redColor : '#0076bf'
    } as React.CSSProperties
  };
}

export default withTheme(TripCardRightTemplate);
